import React, { createContext, memo, useRef, useCallback, useContext, useMemo } from 'react';

type tEvent = (...a: Array<any>) => void | Promise<void>;

type EventListener = (eventName: string, event: tEvent) => void;
type DE = (eventName: string, ...data: Array<any>) => void;

type Events = Record<
  string,
  {
    queue: tEvent[];
  }
>;

const EventBusContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addEventListener: (eventName: string, event: tEvent) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeEventListener: (eventName: string, event: tEvent) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dispatchEvent: (eventName: string, ...data: Array<any>) => {},
});

function useEventBus(): IeventBus {
  const { addEventListener, removeEventListener, dispatchEvent } = useContext(EventBusContext);
  return useMemo(
    () => ({
      addEventListener,
      removeEventListener,
      dispatchEvent,
    }),
    [addEventListener, removeEventListener, dispatchEvent],
  );
}

const EventBusConsumer: React.FC<{ children: any }> = ({ children }) => {
  const eventsRef = useRef<Events>({});

  const addEventListener = useCallback<EventListener>((eventName, event) => {
    if (!eventsRef.current[eventName]) {
      eventsRef.current[eventName] = {
        queue: [],
      };
    }

    if (eventsRef.current[eventName].queue.indexOf(event) > -1)
      eventsRef.current[eventName].queue[eventsRef.current[eventName].queue.indexOf(event)] = event;
    else eventsRef.current[eventName].queue[eventsRef.current[eventName].queue.length] = event;
  }, []);

  const removeEventListener = useCallback<EventListener>((eventName, event) => {
    if (eventsRef.current[eventName].queue.indexOf(event) > -1)
      eventsRef.current[eventName].queue.splice(
        eventsRef.current[eventName].queue.indexOf(event),
        1,
      );
  }, []);

  const dispatchEvent = useCallback<DE>((eventName, ...data) => {
    if (eventsRef.current[eventName]) {
      eventsRef.current[eventName].queue.forEach(e => {
        e(...data);
      });
    }
  }, []);

  return React.createElement(
    EventBusContext.Provider,
    {
      value: {
        // @ts-ignore
        addEventListener,
        removeEventListener,
        dispatchEvent,
      },
    },
    children,
  );
};

export const EventBusProvider = memo(EventBusConsumer);

export interface IeventBus {
  addEventListener: (eventName: string, event: tEvent) => void;
  removeEventListener: (eventName: string, event: tEvent) => void;
  dispatchEvent: (eventName: string, ...data: any[]) => void;
}

export default useEventBus;
